import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedNumber, FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Link } from 'dibs-elements/exports/Link';
import { Badge } from 'dibs-elements/exports/Badge';

import { SbRespContentModuleSelector } from '../SbRespContentModuleSelector/SbRespContentModuleSelector';
import { moreDetails } from '../../pdp/pdpMessages';
import { viewMore } from '../../shared/sharedMessages';
import { VERTICALS } from '../../constants/uriConstants';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import { authLabels, locations } from '../../utils/tracking/shared/followSearchTracking';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbSharedCreatorTopContent.scss';

import { type SbSharedCreatorTopContent_itemSearch$key } from './__generated__/SbSharedCreatorTopContent_itemSearch.graphql';
import { type SbSharedCreatorTopContent_creatorRootPage$key } from './__generated__/SbSharedCreatorTopContent_creatorRootPage.graphql';
import { type SbSharedCreatorTopContent_user$key } from './__generated__/SbSharedCreatorTopContent_user.graphql';

const creatorRootPageFragment = graphql`
    fragment SbSharedCreatorTopContent_creatorRootPage on CreatorRootPage {
        verticals
        CreatorRootTransactions {
            averageSoldPrice
        }
        creatorRootAttributes {
            topPrimaryL1Style {
                ...SeoLink_linkData
                text
            }
            topPrimaryL1Material {
                ...SeoLink_linkData
                text
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment SbSharedCreatorTopContent_itemSearch on ItemSearchQueryConnection {
        relatedCreators {
            edges {
                node {
                    title
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        }
        browseContent: browseContentModule {
            ... on ContentModuleRegular {
                title
            }
        }
        ...SbRespContentModuleSelector_itemSearch
        ...SaveSearchButton_itemSearch
    }
`;

const userFragment = graphql`
    fragment SbSharedCreatorTopContent_user on User {
        ...SbRespContentModuleSelector_user
    }
`;

const CREATOR_TAGS_DISPLAY_NUM = 5;

type Props = {
    itemSearch: SbSharedCreatorTopContent_itemSearch$key;
    creatorRootPage: SbSharedCreatorTopContent_creatorRootPage$key;
    user: SbSharedCreatorTopContent_user$key;
    onClickViewMoreCreators: () => void;
    onClickViewMoreSales: () => void;
    isMobile?: boolean;
    currency?: string;
};

export const SbSharedCreatorTopContent: FC<Props> = ({
    itemSearch: itemSearchRef,
    creatorRootPage: creatorRootPageRef,
    user: userRef,
    onClickViewMoreCreators,
    onClickViewMoreSales,
    isMobile,
    currency,
}) => {
    const creatorRootPage = useFragment(creatorRootPageFragment, creatorRootPageRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);

    const relatedCreators = itemSearch?.relatedCreators?.edges || [];
    const creatorRootAttributes = creatorRootPage?.creatorRootAttributes;
    const avgSoldPriceInfo = creatorRootPage?.CreatorRootTransactions?.averageSoldPrice;
    const styleInfo = creatorRootAttributes?.topPrimaryL1Style || [];
    const materialInfo = creatorRootAttributes?.topPrimaryL1Material || [];
    const topVertical = creatorRootPage?.verticals?.[0];
    const browseContent = itemSearch?.browseContent;
    let infoRightBlockAdditionalStyle = '';

    const expandableContent = (
        <SbRespContentModuleSelector
            itemSearch={itemSearch}
            user={user}
            className={styles.infoLeftBlock}
            hidePhoto
        />
    );

    const avgSoldPriceTitle = (
        <FormattedMessage
            id="sb.SbSharedCreatorTopContent.averageSoldPrice"
            defaultMessage="Average Sold Price"
        />
    );
    const avgSoldPriceDetails = avgSoldPriceInfo && (
        <FormattedNumber
            value={avgSoldPriceInfo}
            style="currency"
            currency={currency}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
        />
    );
    const avgSoldPriceLink = (
        <Link onClick={onClickViewMoreSales} dataTn="avg-sold-price-more-details">
            {moreDetails}
        </Link>
    );

    const styleTitle = (
        <FormattedMessage id="sb.SbSharedCreatorTopContent.styles" defaultMessage="Styles" />
    );
    const styleDetails = (
        <td className={classnames(dibsCss.flex, dibsCss.flexRow, dibsCss.flexWrap)}>
            {styleInfo.map((style, idx) => (
                <div key={`creator-top-style-${idx}-div`} className={styles.topAttrVal}>
                    <SeoLink key={`creator-top-style-${idx}`} linkData={style}>
                        {style?.text}
                    </SeoLink>
                </div>
            ))}
        </td>
    );

    const materialTitle = (
        <FormattedMessage id="sb.SbSharedCreatorTopContent.materials" defaultMessage="Materials" />
    );

    const materialDetails = (
        <td className={classnames(dibsCss.flex, dibsCss.flexRow, dibsCss.flexWrap)}>
            {materialInfo.map((material, idx) => {
                return (
                    <div key={`creator-top-material-${idx}-div`} className={styles.topAttrVal}>
                        <SeoLink key={`creator-top-material-${idx}`} linkData={material}>
                            {material?.text}
                        </SeoLink>
                    </div>
                );
            })}
        </td>
    );

    let relatedCreatorsTitle = (
        <FormattedMessage
            id="sb.SbSharedCreatorTopContent.relatedCreators"
            defaultMessage="Related Creators"
        />
    );

    if (topVertical === VERTICALS.ART) {
        relatedCreatorsTitle = (
            <FormattedMessage
                id="sb.SbSharedCreatorTopContent.relatedArtists"
                defaultMessage="Related Artists"
            />
        );
    } else if (topVertical === VERTICALS.FASHION) {
        relatedCreatorsTitle = (
            <FormattedMessage
                id="sb.SbSharedCreatorTopContent.relatedDesigners"
                defaultMessage="Related Designers"
            />
        );
    }

    const relatedCreatorsLinks = (
        <table className={styles.relatedCreatorsBagdes}>
            <tbody>
                <tr className={dibsCss.sassyFontBodyTypeHeavy}>
                    <td>{relatedCreatorsTitle}</td>
                </tr>
                <tr>
                    <td
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.flexWrap,
                            dibsCss.ptXsmall,
                            dibsCss.gapXsmall
                        )}
                    >
                        {relatedCreators.slice(0, CREATOR_TAGS_DISPLAY_NUM).map(
                            (creator, idx) =>
                                creator?.node && (
                                    <div
                                        key={`related-creator-${idx}`}
                                        className={classnames(dibsCss.mbXsmall)}
                                    >
                                        <Badge type="pill" size="medium-regular" interactive>
                                            <SeoLink
                                                className={styles.creatorTagLink}
                                                linkData={creator.node.linkData}
                                            >
                                                {creator?.node?.title}
                                            </SeoLink>
                                        </Badge>
                                    </div>
                                )
                        )}
                    </td>
                </tr>
                <tr>
                    <td>
                        <Link
                            className={classnames(
                                styles.topContentViewMore,
                                dibsCss.textStart,
                                dibsCss.sassyFontBodySmall
                            )}
                            onClick={onClickViewMoreCreators}
                            dataTn="related-creators-view-more"
                        >
                            {viewMore}
                        </Link>
                    </td>
                </tr>
            </tbody>
        </table>
    );

    if (!isMobile) {
        if (browseContent && browseContent?.title) {
            infoRightBlockAdditionalStyle = dibsCss.ptMedium;
        } else if (!browseContent) {
            infoRightBlockAdditionalStyle = dibsCss.ptSmall;
        }
    }

    let displayContent = (
        <>
            {expandableContent}
            <div
                className={classnames(
                    styles.infoRightBlock,
                    dibsCss.grow,
                    dibsCss.shrink0,
                    dibsCss.basis50,
                    infoRightBlockAdditionalStyle
                )}
            >
                <div
                    className={classnames(
                        styles.topInfoDetailsTag,
                        dibsCss.sassyFontBodySizeSmall,
                        dibsCss.flex,
                        dibsCss.flexWrap,
                        dibsCss.itemsStart,
                        dibsCss.gapYSmall
                    )}
                >
                    {avgSoldPriceInfo && (
                        <table
                            className={classnames(
                                styles.topInfoDetailsTagTable,
                                dibsCss.grow,
                                dibsCss.shrink0,
                                dibsCss.maxWFit
                            )}
                        >
                            <tbody>
                                <tr className={dibsCss.sassyFontBodyTypeHeavy}>
                                    <td>{avgSoldPriceTitle}</td>
                                </tr>
                                <tr>
                                    <td>{avgSoldPriceDetails}</td>
                                </tr>
                                <tr>
                                    <td>{avgSoldPriceLink}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                    {styleInfo.length > 0 && (
                        <table
                            className={classnames(
                                styles.topInfoDetailsTagTable,
                                dibsCss.grow,
                                dibsCss.shrink0,
                                dibsCss.maxWFit
                            )}
                        >
                            <tbody>
                                <tr className={dibsCss.sassyFontBodyTypeHeavy}>
                                    <td>{styleTitle}</td>
                                </tr>
                                <tr>{styleDetails}</tr>
                            </tbody>
                        </table>
                    )}
                    {materialInfo.length > 0 && (
                        <table
                            className={classnames(
                                styles.topInfoDetailsTagTable,
                                dibsCss.grow,
                                dibsCss.shrink0,
                                dibsCss.maxWFit
                            )}
                        >
                            <tbody>
                                <tr className={dibsCss.sassyFontBodyTypeHeavy}>
                                    <td>{materialTitle}</td>
                                </tr>
                                <tr>{materialDetails}</tr>
                            </tbody>
                        </table>
                    )}
                    {relatedCreators.length > 0 && relatedCreatorsLinks}
                </div>
            </div>
        </>
    );

    if (isMobile) {
        displayContent = (
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.mtSmall,
                    dibsCss.alignTop
                )}
            >
                {expandableContent}

                <div className={styles.mobileFollow}>
                    <SaveSearchButton
                        itemSearch={itemSearch}
                        fullWidth
                        locationLabel={authLabels[locations.SEARCH_BROWSE]}
                        size="medium"
                        type="secondary"
                    />
                </div>
                <div className={classnames(dibsCss.ptMedium, dibsCss.mbLarge, styles.infoBlockRow)}>
                    <table>
                        <tbody>
                            {avgSoldPriceInfo && (
                                <tr
                                    className={classnames(
                                        styles.topInfoDetails,
                                        dibsCss.prLarger,
                                        dibsCss.alignTop,
                                        dibsCss.sassyFontBodySizeMedium
                                    )}
                                >
                                    <td
                                        className={classnames(
                                            styles.mobileLabel,
                                            dibsCss.sassyFontBodyTypeHeavy
                                        )}
                                    >
                                        {avgSoldPriceTitle}
                                    </td>
                                    <td>{avgSoldPriceDetails}</td>
                                    <td className={styles.mobileViewMore}>{avgSoldPriceLink}</td>
                                </tr>
                            )}
                            {styleInfo.length > 0 && (
                                <tr
                                    className={classnames(
                                        styles.topInfoDetails,
                                        dibsCss.prLarger,
                                        dibsCss.alignTop,
                                        dibsCss.sassyFontBodySizeMedium
                                    )}
                                >
                                    <td
                                        className={classnames(
                                            styles.mobileLabel,
                                            dibsCss.sassyFontBodyTypeHeavy
                                        )}
                                    >
                                        {styleTitle}
                                    </td>
                                    {styleDetails}
                                </tr>
                            )}
                            {materialInfo.length > 0 && (
                                <tr
                                    className={classnames(
                                        styles.topInfoDetails,
                                        dibsCss.prLarger,
                                        dibsCss.alignTop,
                                        dibsCss.sassyFontBodySizeSmall
                                    )}
                                >
                                    <td
                                        className={classnames(
                                            styles.mobileLabel,
                                            dibsCss.sassyFontBodyTypeHeavy
                                        )}
                                    >
                                        {materialTitle}
                                    </td>
                                    {materialDetails}
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {relatedCreators.length > 0 && relatedCreatorsLinks}
                </div>
            </div>
        );
    }

    return <div className={dibsCss.flex}>{displayContent}</div>;
};
